import { SSO_PROVIDER, type SsoProviderId } from "@/components/SSOAuthButton/types";
import { getCookie } from "@/services/CookiesService";
import { NEXT_LOCALE } from "@/internals/constants/cookies";
import { UTMS } from "@/components/StoreUtms/types";
import type { QREditorContextProps } from "@/contexts/QREditorContext/types";
import { QRType } from "@/types/qr";

export interface BaseAuthPayload {
  oauth_access_token: string;
  reference?: string;
}

export interface GoogleLoginPayload extends BaseAuthPayload {
  // Google login doesn't need extra fields beyond base
}

export interface GoogleRegisterPayload extends BaseAuthPayload {
  locale?: string;
  // UTM parameters spread dynamically
  [key: string]: unknown;
}

export interface AppleAuthPayload extends BaseAuthPayload {
  locale?: string;
  [key: string]: unknown;
}

export interface MicrosoftAuthPayload extends BaseAuthPayload {
  locale?: string;
  [key: string]: unknown;
}

/**
 * Builds authentication payloads for different SSO providers and auth types.
 * Centralizes the logic for constructing API payloads to avoid duplication.
 */
export class AuthPayloadBuilder {
  static buildLoginPayload(
    provider: SsoProviderId,
    accessToken: string,
    qrContext?: QREditorContextProps<QRType | undefined> | null
  ): BaseAuthPayload {
    const basePayload: BaseAuthPayload = {
      oauth_access_token: accessToken,
      reference: qrContext?.qrData?.qrCodeReference,
    };

    switch (provider) {
      case SSO_PROVIDER.GOOGLE:
        return basePayload as GoogleLoginPayload;

      case SSO_PROVIDER.APPLE:
        return basePayload as AppleAuthPayload;

      case SSO_PROVIDER.MICROSOFT:
        return basePayload as MicrosoftAuthPayload;

      default:
        return basePayload;
    }
  }

  static buildRegisterPayload(
    provider: SsoProviderId,
    accessToken: string,
    qrContext?: QREditorContextProps<QRType | undefined> | null
  ): BaseAuthPayload {
    const basePayload: BaseAuthPayload = {
      oauth_access_token: accessToken,
      reference: qrContext?.qrData?.qrCodeReference,
    };

    switch (provider) {
      case SSO_PROVIDER.APPLE:
      case SSO_PROVIDER.GOOGLE:
      case SSO_PROVIDER.MICROSOFT: {
        const utms = getCookie(UTMS) as Record<string, unknown>;
        return {
          ...basePayload,
          locale: getCookie(NEXT_LOCALE) as string,
          ...(utms || {}),
        } as GoogleRegisterPayload | AppleAuthPayload | MicrosoftAuthPayload;
      }

      default:
        return basePayload;
    }
  }
}
